<template>
  <!-- The admin rail — the console SidebarNav's grammar (the current item's
       glyph in the state disc, same classes) so the panel reads as family.
       One live entry today; A2 (Utilisateurs, Santé) grows it — no
       placeholder rows for unbuilt pages. -->
  <nav class="flex flex-col gap-0.5 px-2 py-4">
    <!-- An external entry leaves the SPA (a server-rendered page on this same
         host, or another host in a new tab), so it is a plain anchor: the
         router never owns it and it is never the active one. -->
    <component
      :is="item.external ? 'a' : 'router-link'"
      v-for="item in items"
      :key="item.href"
      v-bind="item.external ? anchorAttrs(item) : { to: item.href }"
      class="flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-foreground transition-colors"
      :class="[
        isActive(item)
          ? 'cursor-default font-semibold text-state-ink'
          : 'font-medium hover:bg-sunken'
      ]"
    >
      <span
        v-if="isActive(item)"
        class="-my-1 -ml-1 grid size-6 shrink-0 place-items-center rounded-full bg-state text-state-foreground"
      >
        <component :is="item.icon" class="size-[13px]" :stroke-width="2.5" />
      </span>
      <component :is="item.icon" v-else class="size-4 shrink-0" />
      {{ item.title }}
    </component>
  </nav>
</template>

<script setup lang="ts">
import { computed, type Component } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Activity, Bug, Building2 } from 'lucide-vue-next'

interface NavItem {
  title: string
  href: string
  icon: Component
  // Extra path prefixes that light this entry (detail pages).
  activePrefixes?: string[]
  // A page outside the SPA (the GoodJob dashboard on this host, GlitchTip on
  // its own): a full navigation, never a router entry.
  external?: boolean
  // Opens in a new tab — another app with its own login, not part of this one.
  newTab?: boolean
}

const { t } = useI18n()
const route = useRoute()

// GlitchTip, the error tracker (docker/glitchtip/). Its own app on its own
// host, so it opens in a new tab; absent outside a production build.
const errorTrackerUrl = import.meta.env.VITE_ERROR_TRACKER_URL

const items = computed<NavItem[]>(() => [
  {
    title: t('admin.organizations.title'),
    href: '/',
    icon: Building2,
    activePrefixes: [ '/organizations' ],
  },
  {
    title: t('admin.goodJob.title'),
    href: '/good_job',
    icon: Activity,
    external: true,
  },
  ...(errorTrackerUrl
    ? [ { title: t('admin.errors.title'), href: errorTrackerUrl, icon: Bug, external: true, newTab: true } ]
    : []),
])

const anchorAttrs = (item: NavItem) =>
  item.newTab
    ? { href: item.href, target: '_blank', rel: 'noopener noreferrer' }
    : { href: item.href }

const isActive = (item: NavItem) =>
  !item.external && (
    route.path === item.href ||
    (item.activePrefixes ?? []).some(prefix => route.path.startsWith(prefix))
  )
</script>
